import { Component, Element, Prop, h, forceUpdate } from "@stencil/core";
import {
    ContentDataProvider,
    getContentDataProvider,
} from "../../../apis/stateful-content";

import {
    getTranslateFn,
    TranslateFn,
    getTranslationSource,
    TranslationRenderType,
} from "../stateful-content-utils";

@Component({
    tag: "ux-iws-stateful-tiles",
    styleUrl: "stateful-tiles.scss",
    shadow: false,
})
export class MyTileContainer {
    private dataProvider: ContentDataProvider;
    private translate: TranslateFn;
    private translationRenderType: TranslationRenderType =
        TranslationRenderType.Content;

    @Element() domNode: HTMLUxIwsStatefulTilesElement;

    /** scope used to retrieve the content data provider */
    @Prop() scope: string = "";

    /** endpoint for content data */
    @Prop() url: string = "";

    componentWillLoad() {
        this.dataProvider = getContentDataProvider(this.scope);
        this.dataProvider.subscribe(this.handleDataProviderState);
        if (this.url !== "") {
            this.dataProvider.setEndpoint(this.url);
        }
        this.setTranslations();
    }

    /**
     * handle script tag translations as well as deprecated translations prop fallback
     **/
    private setTranslations = () => {
        this.translate = getTranslateFn(
            getTranslationSource(
                this.scope,
                this.domNode,
                this.translationRenderType
            )
        );
    };

    private handleDataProviderState = () => {
        forceUpdate(this);
    };

    render() {
        if (
            this.dataProvider === undefined ||
            this.dataProvider.data === null
        ) {
            return null;
        }
        const { data } = this.dataProvider;
        const serviceCaseCreationDisabled = data.isCreateCaseAllowed === false;

        return (
            <div class="ux-row ux-iws-stateful-overview__tile-container">
                {data.items.map((item) => {
                    const hasDiscontinuationInfo =
                        item.discontinued !== undefined;
                    return (
                        <ux-iws-tile
                            class={`ux-col-12 ux-col-md-6 ux-col-xl-4 ux-u-mb-xs ${
                                hasDiscontinuationInfo && "is-discontinued"
                            }`}
                            machine-name={item.name}
                            custom-machine-name={item.customName}
                            {...(data.schema?.number?.type !== "hidden" && {
                                "machine-id": item.number,
                            })}
                            machine-image-url={item.image}
                            auth-image={data.authImages}
                            detail-link={item.detailLink}
                            toggle-fav-url={item.isFav.toggleUrl}
                            is-fav={item.isFav.active}
                            {...(item.serviceCallModalLink && {
                                "service-call-modal-link":
                                    item.serviceCallModalLink,
                            })}
                            {...(serviceCaseCreationDisabled && {
                                "service-case-creation-disabled":
                                    serviceCaseCreationDisabled,
                            })}
                            {...(item.inProduction && {
                                "in-production": item.inProduction,
                            })}
                            {...(item.softwareUpdates && {
                                "software-updates": item.softwareUpdates,
                            })}
                            {...(item.shopLinks?.[0]?.link && {
                                "shop-link": item.shopLinks[0].link,
                            })}
                            {...(item.shopLinks?.[0]?.linkText && {
                                "shop-link-title": item.shopLinks[0].linkText,
                            })}
                            {...(item.shopLinks?.[0]?.shopType && {
                                "shop-link-type": item.shopLinks[0].shopType,
                            })}
                            {...(item.shopLinks?.[0]?.linkTarget && {
                                "shop-link-target":
                                    item.shopLinks[0].linkTarget,
                            })}
                            {...(item.shopLinks?.[1]?.link && {
                                "shop-link-2": item.shopLinks[1].link,
                            })}
                            {...(item.shopLinks?.[1]?.linkText && {
                                "shop-link-2-title": item.shopLinks[1].linkText,
                            })}
                            {...(item.shopLinks?.[1]?.shopType && {
                                "shop-link-2-type": item.shopLinks[1].shopType,
                            })}
                            {...(item.shopLinks?.[1]?.linkTarget && {
                                "shop-link-2-target":
                                    item.shopLinks[1].linkTarget,
                            })}
                            {...(item.chooseShopTitle && {
                                "choose-shop-title": item.chooseShopTitle,
                            })}
                            sync-id={item.number}
                            translateFn={this.translate}
                        >
                            {hasDiscontinuationInfo && (
                                <div slot="discontinuation-icon">
                                    <ux-iws-discontinuation-icon
                                        tooltip-identifier={item.number}
                                        type={item.discontinued.type}
                                        status={item.discontinued.status}
                                        extended-support={
                                            item.discontinued.extendedSupport
                                        }
                                    ></ux-iws-discontinuation-icon>
                                    <ux-iws-discontinuation-tooltip
                                        tooltip-identifier={item.number}
                                        type={item.discontinued.type}
                                        status={item.discontinued.status}
                                        extended-support={
                                            item.discontinued.extendedSupport
                                        }
                                        head={item.discontinued.head}
                                        text={item.discontinued.text}
                                        link={item.discontinued.link}
                                        link-text={item.discontinued.linkText}
                                    ></ux-iws-discontinuation-tooltip>
                                </div>
                            )}
                        </ux-iws-tile>
                    );
                })}
            </div>
        );
    }
}
